/**
 * Email Validation Utility
 *
 * Client-side email format **live feedback** matching the server-side
 * `validate_email_tld`. Auto-attaches to all `input[type=email]` within
 * `.field-group` wrappers and only renders an error visually on `blur`.
 *
 * It deliberately does NOT block the form's `submit` event:
 *
 *   * Server-side validation is the single source of truth (`EmailField` +
 *     `validate_email_tld` everywhere it matters: Organization, Customer,
 *     Contact, Cleaner, signup, invite, ...).
 *   * Forms render `.field-group.has-error` blocks on round-trip, and the
 *     global `form-error-scroll` helper scrolls the first one into view
 *     after `htmx:afterSwap`, so the UX is identical.
 *   * Mixing `e.preventDefault()` here with the global
 *     `data-protect-submit` machinery in `utils/submit-button.js` produced
 *     spinner-state inconsistencies — see the team-form migration
 *     (frontend/src/pages/team-form.js) for the canonical pattern.
 */

var EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/**
 * Check whether a value looks like a valid email address.
 * Mirrors the server-side validate_email_tld: requires @, a domain with a dot,
 * and a TLD of at least 2 characters.
 */
export function isValidEmail(value) {
    if (!value) return false;
    return EMAIL_REGEX.test(value.trim());
}

function getErrorMessage() {
    var body = document.body;
    return (body && body.getAttribute('data-email-error')) || 'Enter a valid email address.';
}

function showFieldError(fieldGroup, input, msg) {
    fieldGroup.classList.add('has-error');
    input.classList.add('border-red-500', 'dark:border-red-500');
    input.classList.remove('border-gray-300', 'dark:border-gray-600');
    if (!fieldGroup.querySelector('.field-error')) {
        var p = document.createElement('p');
        p.className = 'mt-1 text-sm text-red-600 dark:text-red-500 field-error';
        p.textContent = msg;
        // Always append to the field-group (not input.parentNode), so the error
        // renders below the entire widget — not inside an icon wrapper when the
        // input is wrapped in a .relative container for a leading SVG icon.
        fieldGroup.appendChild(p);
    }
}

/**
 * Auto-discover all email inputs inside `.field-group` wrappers and attach
 * non-blocking blur feedback. Call once on `DOMContentLoaded`, and again
 * after dynamic insertion (e.g. when the customer form adds a contact row).
 *
 * The handler is idempotent — already-wired inputs are skipped via
 * `dataset.emailValidation`.
 */
export function initEmailValidation() {
    var msg = getErrorMessage();
    var inputs = document.querySelectorAll('.field-group input[type="email"]');

    inputs.forEach(function (input) {
        if (input.dataset.emailValidation === 'attached') return;
        input.dataset.emailValidation = 'attached';

        var fieldGroup = input.closest('.field-group');
        if (!fieldGroup) return;

        input.addEventListener('blur', function () {
            var val = input.value.trim();
            if (val && !isValidEmail(val)) {
                showFieldError(fieldGroup, input, msg);
            }
        });
    });
}
